import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../services/api";
import { getAll } from "../services/pagination";
import { usePermissions } from "../hooks/usePermissions";
type Application = {
  id: number;
  application_number: string;
  first_name: string;
  last_name: string;
  class_applied_for: number;
  guardian_name: string;
  guardian_phone: string;
  status: string;
  decision_notes: string;
  created_at: string;
};
export default function Admissions() {
  const qc = useQueryClient();
  const { hasCapability } = usePermissions();
  const canDecide = hasCapability("access.admissions.decide_applications");
  const canEnroll = hasCapability("access.admissions.enroll_applicants");
  const [enrollmentResult,setEnrollmentResult] = useState<{student_id:string;admission_number:string;credentials:Record<string,{username:string;temporary_password:string}>}|null>(null);
  const applications = useQuery({
    queryKey: ["admissions"],
    queryFn: () => getAll<Application>("/admissions/applications/"),
  });
  const decision = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.post(`/admissions/applications/${id}/decision/`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admissions"] }),
  });
  const enroll = useMutation({mutationFn: async(id:number)=>(await api.post(`/admissions/applications/${id}/enroll/`,{admission_date:new Date().toISOString().slice(0,10)})).data,onSuccess:(data)=>{setEnrollmentResult(data);qc.invalidateQueries({queryKey:["admissions"]})}});
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Admissions</h1>
        <p className="text-gray-600">
          Review online applications and record decisions.
        </p>
      </div>
      {enrollmentResult && <div className="rounded border border-green-200 bg-green-50 p-4"><h2 className="font-semibold">Learner enrolled · {enrollmentResult.student_id} / {enrollmentResult.admission_number}</h2>{Object.entries(enrollmentResult.credentials).map(([type,value])=><div key={type} className="mt-2 font-mono text-sm capitalize">{type}: {value.username} · {value.temporary_password}</div>)}<p className="mt-2 text-sm">Copy these temporary credentials now. Each user must change their password at first login.</p><button onClick={()=>setEnrollmentResult(null)} className="btn btn-outline mt-2">Credentials copied</button></div>}
      <div className="card overflow-x-auto">
        <table className="min-w-full divide-y">
          <thead>
            <tr>
              {["Application", "Learner", "Guardian", "Status", "Decision"].map(
                (h) => (
                  <th
                    key={h}
                    className="p-4 text-left text-xs uppercase text-gray-500"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {applications.data?.map((a) => (
              <tr key={a.id}>
                <td className="p-4 font-medium">{a.application_number}</td>
                <td className="p-4">
                  {a.first_name} {a.last_name}
                </td>
                <td className="p-4">
                  {a.guardian_name}
                  <div className="text-xs text-gray-500">
                    {a.guardian_phone}
                  </div>
                </td>
                <td className="p-4 capitalize">{a.status.replace("_", " ")}</td>
                <td className="p-4">
                  <div className="space-y-2">{canDecide ? <select
                    aria-label={`Decision for ${a.application_number}`}
                    value={a.status}
                    onChange={(e) =>
                      decision.mutate({ id: a.id, status: e.target.value })
                    }
                    disabled={["accepted","enrolled"].includes(a.status)} className="input"
                  >
                    <option value="submitted">Submitted</option>
                    <option value="under_review">Under review</option>
                    <option value="documents_required">
                      Documents required
                    </option>
                    <option value="offered">Offer place</option>
                    <option value="rejected">Reject</option>
                    <option value="withdrawn">Withdrawn</option>
                    <option value="accepted">Accepted by guardian</option><option value="enrolled">Enrolled</option>
                  </select> : <span className="text-sm capitalize">{a.status.replace("_"," ")}</span>}
                  {canEnroll && a.status === "accepted" && <button onClick={()=>enroll.mutate(a.id)} className="btn btn-primary w-full">Create student and guardian</button>}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!applications.data?.length && (
          <div className="p-8 text-center text-gray-500">
            No applications submitted.
          </div>
        )}
      </div>
    </div>
  );
}
